import React, { useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  Mail,
  MessageSquare,
  MapPin,
  Phone,
  Send,
  CheckCircle2,
  Clock,
  Headphones,
  Building2
} from 'lucide-react';
import PageLayout, { PageHero } from './layout/PageLayout';
import SEO from './SEO';
import { BACKEND_URL } from '../utils/backendURL';
import { showSuccess, showError } from '../utils/notifications';

const ContactPage = ({ onBack, onLogin, onRegister }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitted(true);
      showSuccess('Message sent successfully! We\'ll get back to you soon.');
    } catch (error) {
      showError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email',
      description: 'Send us an email anytime',
      value: 'hello@lexa.run',
      action: 'mailto:hello@lexa.run',
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      icon: MessageSquare,
      title: 'Sales Questions',
      description: 'Discuss your product questions',
      value: 'hello@lexa.run',
      action: 'mailto:hello@lexa.run',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: Headphones,
      title: 'Support',
      description: 'Get help with technical issues',
      value: 'support@lexa.run',
      action: 'mailto:support@lexa.run',
      gradient: 'from-amber-500 to-orange-500'
    }
  ];

  const offices = [
    {
      city: 'Remote-First',
      country: 'Global',
      description: 'Contact the team online',
      icon: Building2
    }
  ];

  return (
    <PageLayout onBack={onBack} onLogin={onLogin} onRegister={onRegister}>
      <SEO 
        title="Contact Us - Lexa.run | Get in Touch"
        description="Have questions about Lexa.run? Contact our team for sales inquiries, support, or partnership opportunities. We're here to help."
      />

      <PageHero
        badge={<><MessageSquare className="w-4 h-4" /> Get in Touch</>}
        title="We'd love to"
        titleHighlight="hear from you"
        subtitle="Have questions about Lexa.run? Our team is here to help with sales, support, or partnership inquiries."
      />

      {/* Contact Methods */}
      <section className="py-16 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactMethods.map((method, idx) => (
              <a 
                key={idx}
                href={method.action}
                className="group"
              >
                <Card className="p-8 rounded-3xl border border-gray-200 hover:shadow-xl hover:border-purple-200 transition-all duration-300 h-full">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${method.gradient} text-white mb-6 group-hover:scale-110 transition-transform`}>
                    <method.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-gray-500 text-sm mb-3">{method.description}</p>
                  <p className="text-purple-600 font-medium">{method.value}</p>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              <Badge variant="outline" className="mb-4 text-purple-600 border-purple-200">
                <Send className="w-4 h-4 mr-1" /> Send a Message
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                Drop us a line
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form and our team will get back to you by email.
              </p>

              {submitted ? (
                <Card className="p-8 rounded-3xl border border-green-200 bg-green-50 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600">
                    Thank you for reaching out. We'll get back to you by email.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="mt-6"
                  >
                    Send Another Message
                  </Button>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Name *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="rounded-xl h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        required
                        className="rounded-xl h-12"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <Input
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Company Inc."
                        className="rounded-xl h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        required
                        className="rounded-xl h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us more about your inquiry..."
                      required
                      className="rounded-xl min-h-[150px]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-6 text-lg font-semibold shadow-lg shadow-purple-500/25"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Info Cards */}
            <div className="space-y-8">
              <Card className="p-8 rounded-3xl border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-purple-100">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Response Time</h3>
                    <p className="text-gray-600">
                      Our team reviews inquiries and follows up by email.
                      Include the affected workspace and a description of the issue.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 rounded-3xl border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-green-100">
                    <Headphones className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Enterprise Support</h3>
                    <p className="text-gray-600">
                      Contact us to discuss support requirements for your organization.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-8 rounded-3xl border border-gray-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-blue-100">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Our Location</h3>
                    <p className="text-gray-600">
                      You can reach our team online.
                      Send your questions using the form or email addresses above.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default ContactPage;
